import React, { useState } from "react";
import { useGetSupplierOverviewData } from "../../../../../hooks/query/emissions-insight/emissions-insights-query";
import { constructQueryParamsString, showErrorToast } from "../../../../../utils";
import { Box, Flex, Text, useToast } from "@chakra-ui/react";
import { useGetSuppliersYearlyData } from "../../../../../hooks/query";
import { STATUSFIELD_VALUE_LABEL } from "../../../../ISSB/IssbDataInput/components/AdditionalStatuses";
import { calculateLimeGradientColor, STAGETYPE_LABEL } from "../../../../ISSB/Dashboard/dashboardDataUtils";
import { nivo_theme } from "../../../../ISSB/Dashboard/components/chart-utils";
import { ResponsiveHeatMap } from "@nivo/heatmap";
import LocalSpinner from "../../../../../components/LocalSpinner";

const TrendVisual = ({ filters, setFilters }) => {
  const toast = useToast();

  const [yearByYearEmissionsData, setYearByYearEmissionsData] = useState([]);

  const getQueryParamsString = () => {
    let year = filters?.year;
    if (!year) {
      // HACK default - which will also get set when filters is initialised
      year = new Date().getFullYear();
    }
    const currentYear = new Date().getFullYear();
    const yearsSet = new Set([currentYear, year]);
    let yearToAdd = currentYear - 1;
    while (yearsSet.size < 5 && yearToAdd >= 2021) {
      // assuming data is available from 2021 onwards
      yearsSet.add(yearToAdd);
      yearToAdd--;
    }
    const years = Array.from(yearsSet).sort((a, b) => b - a);

    const queryParams = {
      years: years,
      categories: filters?.categories,
      dataQuality: filters?.dataQuality,
    };
    return constructQueryParamsString(queryParams);
  };

  const constructHeatMapData = (suppliers) => {
    const yearCompletionMap = {};
    const supplierMap = {};

    suppliers.forEach(({ yearlyEmissionData, supplierId, supplierName }) => {
      // Count number of verified years
      const verifiedYears = Object.values(yearlyEmissionData || {}).filter((y) => y?.dataQuality === "VERIFIED").length;

      // Count per "verified year" group (x) and per total completed years (id)
      const completedYears = Object.values(yearlyEmissionData || {}).filter((y) => y?.totalCo2Emissions > 0).length;

      const xKey = verifiedYears; // now X-axis is number of verified years
      const countKey = completedYears; // Y-axis group (id)

      if (!yearCompletionMap[countKey]) yearCompletionMap[countKey] = {};
      if (!yearCompletionMap[countKey][xKey]) yearCompletionMap[countKey][xKey] = 0;
      if (!supplierMap[countKey]) supplierMap[countKey] = {};
      if (!supplierMap[countKey][xKey]) supplierMap[countKey][xKey] = [];

      yearCompletionMap[countKey][xKey] += 1;
      supplierMap[countKey][xKey].push({ supplierId, supplierName });
    });

    // Color scale
    const allValues = Object.values(yearCompletionMap).flatMap((o) => Object.values(o));
    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);


    return Object.entries(yearCompletionMap).map(([yearsCompleted, xMap]) => ({
      id: `${yearsCompleted}`,
      data: Object.entries(xMap).map(([verifiedYears, value]) => ({
        x: `${verifiedYears}`,
        y: value,
        color: calculateLimeGradientColor(value, minValue, maxValue),
        suppliers: supplierMap[yearsCompleted][verifiedYears] || [],
      })),
    }));
  };

  const { isFetching } = useGetSuppliersYearlyData(getQueryParamsString(), {
    onSuccess: (data) => {
      // exclude data where excludeFromReport is true
      const filteredData = data.filter((item) => !item.excludeFromReport);
      setYearByYearEmissionsData(constructHeatMapData(filteredData));
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  if (isFetching) {
    return <LocalSpinner />;
  }

  return (
    <Box height="500px">
      <ResponsiveHeatMap
        data={yearByYearEmissionsData}
        margin={{ top: 60, right: 70, bottom: 60, left: 120 }}
        valueFormat=".0f"
        axisTop={{
          tickSize: 0,
          tickPadding: 5,
          // tickRotation: -90,
          tickValues: [],
          legend: "",
          legendPosition: "middle",
          legendOffset: -50,
          truncateTickAt: 0,
          format: (d) => STATUSFIELD_VALUE_LABEL.confidence[d] ?? d,
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: "Years Complete",
          legendPosition: "middle",
          legendOffset: -50,
          truncateTickAt: 0,
          format: (d) => STAGETYPE_LABEL[d] ?? d,
        }}
        axisRight={{
          tickSize: 0,
          tickPadding: 0,
          legend: "",
          legendPosition: "middle",
          legendOffset: 20,
          tickValues: [],
        }}
        colors={(d) => d.data.color.color}
        labelTextColor={(d) => d.data.color.textColor}
        axisBottom={{
          tickSize: 5,
          tickPadding: 0,
          legend: "Years Verified",
          legendPosition: "middle",
          legendOffset: 50,
        }}
        tooltip={({ cell }) => {
          return (
            <CustomTooltip
              suppliers={cell?.data?.suppliers}
            />
          );
        }}
        emptyColor="#555555"
        theme={nivo_theme}
      />
    </Box>
  );
};

const CustomTooltip = ({ suppliers }) => {
  return (
    <Flex p={2} gap={0} direction="column" borderRadius="md" bg="pureWhite">
      {suppliers?.map((s) => (
        <Text m={0} key={s?.supplierId} p={1} color="darkTeal" fontSize="sm">
          {s?.supplierName}
        </Text>
      ))}
    </Flex>
  );
};

export default TrendVisual;
